import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Save, X, Trash2, Pencil, ExternalLink, Eye } from 'lucide-react';
import '@/styles/journal-admin.css';
import '@/styles/journal.css';
import Markdown from '@/components/Markdown';

const API = process.env.REACT_APP_BACKEND_URL;

const SITE_ASSETS = [
  '/og-home.png', '/og-work.png', '/og-imprint.png', '/og-temple.png', '/og-diamondbrite.png',
];

const CASE_STUDIES = [
  { value: '', label: '— none —' },
  { value: 'diamondbrite', label: 'Diamondbrite', url: '/case-studies/diamondbrite' },
  { value: 'imprint-mis', label: 'Imprint MIS', url: '/case-studies/imprint-mis' },
  { value: 'temple-of-sound', label: 'Temple of Sound', url: '/case-studies/temple-of-sound' },
  { value: 'london-drumstick-co', label: 'London Drumstick Co.', url: '/case-studies/london-drumstick-co' },
];

const EMPTY = {
  title: '', slug: '', excerpt: '', body_md: '', cover_image: '', cover_alt: '',
  tags: [], author: 'Will · Boost!', status: 'draft', seo_title: '', seo_description: '',
  keywords: [], locations: [], case_study: '', case_study_url: '', faqs: [], featured: false,
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 120);
const listToText = (a) => (a || []).join(', ');
const textToList = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

function PostEditor({ token, post, onDone, onCancel }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(post || {}) }));
  const [tagsText, setTagsText] = useState(listToText(post?.tags));
  const [kwText, setKwText] = useState(listToText(post?.keywords));
  const [locText, setLocText] = useState(listToText(post?.locations));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const bodyRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const insert = (before, after = '', placeholder = '') => {
    const ta = bodyRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value } = ta;
    const selected = value.slice(s, e) || placeholder;
    const next = `${value.slice(0, s)}${before}${selected}${after}${value.slice(e)}`;
    set('body_md', next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = s + before.length;
      ta.selectionEnd = s + before.length + selected.length;
    });
  };

  const save = async () => {
    setBusy(true);
    setError('');
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      tags: textToList(tagsText),
      keywords: textToList(kwText),
      locations: textToList(locText),
      faqs: (form.faqs || []).filter((f) => f.q?.trim() && f.a?.trim()),
    };
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    delete payload.published_at; delete payload.reading_minutes;
    try {
      const res = await fetch(
        post?.id ? `${API}/api/admin/blog/posts/${post.id}` : `${API}/api/admin/blog/posts`,
        {
          method: post?.id ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not save this post. Check the required fields and try again.');
      }
      onDone(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="je-editor" data-testid="journal-editor">
      {error && <p className="je-error" role="alert" data-testid="journal-editor-error">{error}</p>}

      <div className="je-grid">
        <div className="je-field">
          <label htmlFor="je-title">Title</label>
          <input
            id="je-title" value={form.title}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, title: v, slug: post?.id ? f.slug : slugify(v) }));
            }}
            placeholder="How we took Diamondbrite from 2.8★ to 4.9★"
            data-testid="journal-field-title"
          />
        </div>
        <div className="je-field">
          <label htmlFor="je-slug">URL slug</label>
          <input id="je-slug" value={form.slug} onChange={(e) => set('slug', slugify(e.target.value))} data-testid="journal-field-slug" />
          <span className="je-hint">/journal/{form.slug || 'your-slug'}</span>
        </div>
      </div>

      <div className="je-field">
        <label htmlFor="je-excerpt">Excerpt / standfirst</label>
        <textarea id="je-excerpt" rows={3} value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} data-testid="journal-field-excerpt" />
        <span className="je-hint">{form.excerpt.length}/400 — used on cards, social shares and the AI feed.</span>
      </div>

      <div className="je-grid">
        <div className="je-field">
          <label htmlFor="je-cover">Cover image URL</label>
          <input id="je-cover" value={form.cover_image} onChange={(e) => set('cover_image', e.target.value)} placeholder="/og-home.png or https://…" data-testid="journal-field-cover" />
          <div className="je-assets">
            {SITE_ASSETS.map((a) => (
              <button key={a} type="button" onClick={() => set('cover_image', a)} data-testid={`journal-asset-${a.replace(/[^a-z0-9]+/gi, '-')}`}>{a}</button>
            ))}
          </div>
          {form.cover_image && <img className="je-cover-preview" src={form.cover_image} alt="" />}
        </div>
        <div className="je-field">
          <label htmlFor="je-coveralt">Cover image alt text</label>
          <input id="je-coveralt" value={form.cover_alt} onChange={(e) => set('cover_alt', e.target.value)} data-testid="journal-field-cover-alt" />
        </div>
      </div>

      <div className="je-grid">
        <div className="je-field">
          <label htmlFor="je-tags">Tags (comma separated)</label>
          <input id="je-tags" value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="Case Study, Local SEO" data-testid="journal-field-tags" />
        </div>
        <div className="je-field">
          <label htmlFor="je-locs">Locations targeted (comma separated)</label>
          <input id="je-locs" value={locText} onChange={(e) => setLocText(e.target.value)} placeholder="Saffron Walden, Cambridge, Essex" data-testid="journal-field-locations" />
        </div>
      </div>

      <div className="je-field">
        <label htmlFor="je-kw">Local SEO keywords (comma separated)</label>
        <input id="je-kw" value={kwText} onChange={(e) => setKwText(e.target.value)} placeholder="marketing agency Saffron Walden, web design Cambridge" data-testid="journal-field-keywords" />
      </div>

      <div className="je-field">
        <label>Body (markdown)</label>
        <div className="je-md-bar">
          <button type="button" onClick={() => insert('## ', '', 'Section heading')} data-testid="journal-md-h2">H2</button>
          <button type="button" onClick={() => insert('### ', '', 'Sub heading')}>H3</button>
          <button type="button" onClick={() => insert('**', '**', 'bold')} data-testid="journal-md-bold">Bold</button>
          <button type="button" onClick={() => insert('_', '_', 'italic')}>Italic</button>
          <button type="button" onClick={() => insert('- ', '', 'List item')}>List</button>
          <button type="button" onClick={() => insert('> ', '', 'Pull quote')}>Quote</button>
          <button type="button" onClick={() => insert('[', '](/marketing-saffron-walden)', 'link text')} data-testid="journal-md-link">Link</button>
          <button type="button" onClick={() => insert('![alt text](', ')', '/og-home.png')} data-testid="journal-md-image">Image</button>
          <button type="button" onClick={() => insert('\n| Metric | Result |\n| --- | --- |\n| ', ' | value |\n')}>Table</button>
          <button type="button" onClick={() => insert('\n---\n')}>Divider</button>
          <button type="button" onClick={() => setShowPreview((p) => !p)} data-testid="journal-md-preview-toggle">
            <Eye size={13} /> {showPreview ? 'Hide' : 'Show'} preview
          </button>
        </div>
        <div className={showPreview ? 'je-md-split' : ''}>
          <textarea
            ref={bodyRef}
            value={form.body_md}
            onChange={(e) => set('body_md', e.target.value)}
            placeholder="Write the article in markdown…"
            data-testid="journal-field-body"
          />
          {showPreview && (
            <div className="je-preview" data-testid="journal-live-preview">
              <Markdown>{form.body_md}</Markdown>
            </div>
          )}
        </div>
        <span className="je-hint">
          {(form.body_md.match(/\w+/g) || []).length} words · ~{Math.max(1, Math.round((form.body_md.match(/\w+/g) || []).length / 200))} min read
        </span>
      </div>

      <div className="je-grid">
        <div className="je-field">
          <label htmlFor="je-seotitle">SEO title (optional)</label>
          <input id="je-seotitle" value={form.seo_title} onChange={(e) => set('seo_title', e.target.value)} data-testid="journal-field-seo-title" />
          <span className="je-hint">{form.seo_title.length || 0} chars — aim for 50–60.</span>
        </div>
        <div className="je-field">
          <label htmlFor="je-seodesc">Meta description (optional)</label>
          <textarea id="je-seodesc" rows={2} value={form.seo_description} onChange={(e) => set('seo_description', e.target.value)} data-testid="journal-field-seo-description" />
          <span className="je-hint">{form.seo_description.length || 0} chars — aim for 140–160.</span>
        </div>
      </div>

      <div className="je-grid">
        <div className="je-field">
          <label htmlFor="je-cs">Linked case study</label>
          <select
            id="je-cs" value={form.case_study}
            onChange={(e) => {
              const cs = CASE_STUDIES.find((c) => c.value === e.target.value);
              setForm((f) => ({ ...f, case_study: e.target.value, case_study_url: cs?.url || '' }));
            }}
            data-testid="journal-field-case-study"
          >
            {CASE_STUDIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="je-field">
          <label htmlFor="je-author">Author</label>
          <input id="je-author" value={form.author} onChange={(e) => set('author', e.target.value)} data-testid="journal-field-author" />
        </div>
      </div>

      <div className="je-field">
        <label>FAQs (adds FAQ schema — great for Google &amp; AI answers)</label>
        {(form.faqs || []).map((f, i) => (
          <div className="je-faq-row" key={i}>
            <input
              value={f.q} placeholder="Question"
              onChange={(e) => set('faqs', form.faqs.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)))}
              data-testid={`journal-faq-q-${i}`}
            />
            <textarea
              value={f.a} placeholder="Answer"
              onChange={(e) => set('faqs', form.faqs.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))}
              data-testid={`journal-faq-a-${i}`}
            />
            <button type="button" className="je-cancel" onClick={() => set('faqs', form.faqs.filter((_, j) => j !== i))} data-testid={`journal-faq-remove-${i}`}>
              <X size={14} />
            </button>
          </div>
        ))}
        <button type="button" className="je-cancel" onClick={() => set('faqs', [...(form.faqs || []), { q: '', a: '' }])} data-testid="journal-faq-add">
          <Plus size={14} /> Add FAQ
        </button>
      </div>

      <div className="je-grid">
        <div className="je-field">
          <label htmlFor="je-status">Status</label>
          <select id="je-status" value={form.status} onChange={(e) => set('status', e.target.value)} data-testid="journal-field-status">
            <option value="draft">Draft (hidden)</option>
            <option value="published">Published (live, in sitemap, RSS &amp; AI feed)</option>
          </select>
        </div>
        <div className="je-field">
          <label htmlFor="je-featured">Featured</label>
          <select id="je-featured" value={form.featured ? 'yes' : 'no'} onChange={(e) => set('featured', e.target.value === 'yes')} data-testid="journal-field-featured">
            <option value="no">No</option>
            <option value="yes">Yes — highlight this post</option>
          </select>
        </div>
      </div>

      <div className="je-actions">
        <button className="je-save" onClick={save} disabled={busy || !form.title.trim()} data-testid="journal-save">
          <Save size={15} /> {busy ? 'Saving…' : post?.id ? 'Save changes' : 'Create post'}
        </button>
        <button className="je-cancel" onClick={onCancel} data-testid="journal-cancel"><X size={15} /> Cancel</button>
      </div>
    </div>
  );
}

export default function JournalAdmin({ token, onUnauthorised }) {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/blog/posts`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onUnauthorised(); return; }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch {
      setError('Could not load journal posts. Check your connection and hit Refresh.');
    }
  }, [token, onUnauthorised]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Delete this article permanently?')) return;
    try {
      const res = await fetch(`${API}/api/admin/blog/posts/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onUnauthorised(); return; }
      if (!res.ok) throw new Error();
      setPosts((l) => l.filter((p) => p.id !== id));
    } catch {
      setError('Could not delete that article.');
    }
  };

  if (editing !== null) {
    return (
      <PostEditor
        token={token}
        post={editing}
        onCancel={() => setEditing(null)}
        onDone={(saved) => {
          setEditing(null);
          setNotice(`Saved "${saved.title}" — ${saved.status === 'published' ? 'live on /journal' : 'kept as a draft'}.`);
          load();
        }}
      />
    );
  }

  return (
    <div data-testid="journal-admin">
      {error && <p className="je-error" role="alert" data-testid="journal-admin-error">{error}</p>}
      {notice && <p className="je-ok" data-testid="journal-admin-notice">{notice}</p>}
      <div className="je-toolbar">
        <button className="je-save" onClick={() => setEditing({})} data-testid="journal-new-post">
          <Plus size={15} /> New article
        </button>
        <a className="je-cancel" href="/journal" target="_blank" rel="noreferrer" data-testid="journal-view-live">
          <ExternalLink size={15} /> View the Journal
        </a>
      </div>

      {posts === null && <p className="ed-empty">Loading articles…</p>}
      {posts && posts.length === 0 && (
        <p className="ed-empty" data-testid="journal-admin-empty">No articles yet. Hit "New article" to write the first mission log. 🚀</p>
      )}
      <div className="je-list">
        {(posts || []).map((p) => (
          <article className="je-row" key={p.id} data-testid={`journal-row-${p.slug}`}>
            <div className="je-row-main">
              <h3>{p.title}</h3>
              <div className="je-row-meta">
                <span
                  className="je-badge"
                  style={p.status === 'published'
                    ? { background: '#DFF5E9', color: '#17A566' }
                    : { background: '#EDEDF4', color: '#55557A' }}
                  data-testid={`journal-status-${p.slug}`}
                >
                  {p.status === 'published' ? 'Published' : 'Draft'}
                </span>
                <span>/journal/{p.slug}</span>
                <span>{p.reading_minutes} min</span>
                <span>{(p.tags || []).join(' · ')}</span>
              </div>
            </div>
            <div className="je-row-actions">
              <button onClick={() => setEditing(p)} data-testid={`journal-edit-${p.slug}`}><Pencil size={14} /> Edit</button>
              {p.status === 'published' && (
                <a href={`/journal/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`journal-open-${p.slug}`}>
                  <ExternalLink size={14} /> Open
                </a>
              )}
              <button className="je-danger" onClick={() => remove(p.id)} data-testid={`journal-delete-${p.slug}`}><Trash2 size={14} /> Delete</button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
